import {
  Cargo,
  CargoType,
  GenericQueryParamsType,
  GenericResponse,
  ProductGroup,
} from '../types';
import { getHeaders, tosApi } from './tosApi';

type ProductGroupResponse = GenericResponse & {
  results: Array<ProductGroup> | [];
};

type CargoResponse = GenericResponse & {
  results: Array<Cargo> | [];
};
type CargoQueryParamsType = GenericQueryParamsType & {
  name?: string;
};

export const cargoApi = tosApi.injectEndpoints({
  endpoints: (build) => ({
    getCargos: build.query<CargoResponse, GenericQueryParamsType | void>({
      query: (params: GenericQueryParamsType) => {
        return {
          url: `/maritime/cargos`,
          method: 'GET',
          headers: getHeaders,
          params,
        };
      },
      transformResponse: (response: CargoResponse) => {
        response.results.forEach((cargo) => {
          cargo.key = cargo.id;
        });
        return response;
      },
      keepUnusedDataFor: 300,
      providesTags: () => [{ type: 'Cargo', code: '2' }],
    }),
    createCargo: build.mutation<any, Cargo>({
      query: (cargo) => {
        return {
          url: `/maritime/cargos`,
          method: 'POST',
          body: cargo,
        };
      },
      invalidatesTags: () => [{ type: 'Cargo', code: '2' }],
    }),
    updateCargo: build.mutation<
      CargoType,
      Partial<CargoType> & Pick<CargoType, 'id'>
    >({
      query: ({ id, ...cargo }) => {
        return {
          url: `/maritime/cargos/${id}`,
          method: 'PUT',
          body: cargo,
        };
      },
      invalidatesTags: () => [{ type: 'Cargo', code: '2' }],
    }),
    deleteCargo: build.mutation<CargoType, number>({
      query: (cargoId) => {
        return {
          url: `/maritime/cargos/${cargoId}`,
          method: 'DELETE',
        };
      },
    }),
    getProductGroups: build.query<
      ProductGroupResponse,
      {
        nature_of_cargo?: string;
        name?: string;
        page?: number;
        page_size?: number;
      } | void
    >({
      query: (params) => {
        return {
          url: `maritime/product-groups`,
          method: 'GET',
          headers: getHeaders,
          params: params || {},
        };
      },
      providesTags: (result) =>
        result ? [{ type: 'ProductGroup' as const, id: 'LIST' }] : [],
    }),
    createProductGroup: build.mutation<ProductGroup, ProductGroup>({
      query: (productGroup) => {
        return {
          url: `/maritime/product-groups`,
          method: 'POST',
          body: productGroup,
        };
      },
      invalidatesTags: () => [
        { type: 'Cargo', code: '2' },
        { type: 'ProductGroup', id: 'LIST' },
      ],
    }),
    updateProductGroup: build.mutation<
      ProductGroup,
      Partial<ProductGroup> & Pick<ProductGroup, 'id'>
    >({
      query: ({ id, ...body }) => {
        return {
          url: `/maritime/product-groups/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: () => [
        { type: 'Cargo', code: '2' },
        { type: 'ProductGroup', id: 'LIST' },
        { type: 'CargoType', id: 'LIST' },
      ],
    }),
    createCargoType: build.mutation<CargoType, CargoType>({
      query: (cargoType) => {
        return {
          url: `/maritime/cargo-type`,
          method: 'POST',
          body: cargoType,
        };
      },
      invalidatesTags: () => [
        { type: 'Cargo', code: '2' },
        { type: 'CargoType', id: 'LIST' },
      ],
    }),
    updateCargoType: build.mutation<
      CargoType,
      {
        name: string;
        description: string;
        product_group: number;
        docking_places?: string[];
      }
    >({
      query: ({ name, ...body }) => {
        return {
          url: `/maritime/cargo-type/${encodeURIComponent(name)}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'Cargo', code: '2' },
        { type: 'CargoType', id: 'LIST' },
        { type: 'CargoType', id: name },
      ],
    }),
    getCargoTypeWithDockingPlaces: build.query<any, string>({
      query: (name) => {
        return {
          url: `/maritime/cargo-type/with-docking-places/${encodeURIComponent(
            name
          )}`,
          method: 'GET',
          headers: getHeaders,
        };
      },
      providesTags: (result) =>
        result ? [{ type: 'CargoType' as const, id: result.name }] : [],
    }),
    getCargoType: build.query<
      { results: Array<any>; count: number },
      GenericQueryParamsType | void
    >({
      query: (params) => {
        return {
          url: `/maritime/cargo-type`,
          method: 'GET',
          headers: getHeaders,
          params: params || {},
        };
      },
      transformResponse: (response: {
        results: Array<any>;
        count?: number;
      }) => {
        const count = response.count ?? response.results?.length ?? 0;
        response.results.forEach((item) => {
          item.id = item.name;
          if (!item.description) {
            item.description = item.name;
          }
          return item;
        });
        return { ...response, count };
      },
      keepUnusedDataFor: 300,
      providesTags: (result) =>
        result ? [{ type: 'CargoType' as const, id: 'LIST' }] : [],
    }),
  }),
});

export const {
  useGetCargosQuery,
  useCreateCargoMutation,
  useGetProductGroupsQuery,
  useCreateProductGroupMutation,
  useUpdateProductGroupMutation,
  useCreateCargoTypeMutation,
  useUpdateCargoTypeMutation,
  useUpdateCargoMutation,
  useDeleteCargoMutation,
  useGetCargoTypeQuery,
  useGetCargoTypeWithDockingPlacesQuery,
} = cargoApi;
